/**
 * Activity register-results helpers
 *
 * Pure utilities for interpreting the per-slot `Promise.allSettled` batches returned by the
 * activity register actions. Shared by the signup flow (`ActivitySignupModal`) and the
 * manage/edit reconcile flow (`useActivityReconcile`).
 */

import type { SlotInfo } from "../activity-signups/performRegistration";
import { sendMultiSlotConfirmationEmail } from "../actions/signup-actions";
import { REGISTER_NETWORK_RETRY_DELAY_MS } from "../constants";

/** Per-slot result returned by both register actions. */
export type RegisterPromiseResult = PromiseSettledResult<{
  success: boolean;
  waitlisted?: boolean;
  registrationId?: number;
  error?: string;
  slotInfo?: SlotInfo;
}>;

/** Verdict computed from the batch of per-slot results. The caller applies this to its state. */
export interface RegisterVerdict {
  registered: number;
  waitlisted: number;
  failureCount: number;
  /**
   * Server-supplied error from the first failing slot, used to surface
   * actionable messages (e.g. "You are already registered for this time
   * slot") instead of a generic "Failed to register. Please try again."
   */
  firstError?: string;
  /**
   * Rejection reason from the first slot whose register call never completed (network drop,
   * timeout) rather than resolving with a handled `{success:false}`. Undefined when every
   * failure was server-returned. Lets the caller tell "server said no" apart from "the request
   * never got an answer" — previously this was silently dropped, so the latter always fell
   * through to the same generic copy with nothing logged or captured anywhere.
   */
  firstRejection?: unknown;
}

/**
 * Returns the input slotIds whose corresponding result succeeded. Index-aligned with
 * the `Promise.allSettled` inputs in both register paths.
 *
 * @param rs - Settled results, index-aligned with `slotIds`.
 * @param slotIds - The slot ids submitted, in the same order as `rs`.
 * @returns The slot ids whose result fulfilled with `success: true`.
 */
export function succeededSlotIdsFrom(rs: RegisterPromiseResult[], slotIds: number[]): number[] {
  const out: number[] = [];
  rs.forEach((r, i) => {
    if (r.status === "fulfilled" && r.value.success) out.push(slotIds[i]!);
  });
  return out;
}

/**
 * Tally a batch of per-slot register results into a single verdict object.
 *
 * @param rs - Settled per-slot results.
 * @returns Counts of registered/waitlisted/failed plus the first server error and/or rejection.
 */
export function tallyRegisterResults(rs: RegisterPromiseResult[]): RegisterVerdict {
  let registered = 0;
  let waitlisted = 0;
  let failureCount = 0;
  let firstError: string | undefined;
  let firstRejection: unknown;
  for (const r of rs) {
    if (r.status === "fulfilled" && r.value.success) {
      if (r.value.waitlisted) waitlisted++;
      else registered++;
    } else {
      failureCount++;
      if (r.status === "fulfilled") {
        if (!firstError && r.value.error) firstError = r.value.error;
      } else if (firstRejection === undefined) {
        firstRejection = r.reason;
      }
    }
  }
  return { registered, waitlisted, failureCount, firstError, firstRejection };
}

/**
 * Runs `action` once, and once more after a short delay if it rejects outright. Only retries a
 * rejection (network drop/timeout) — a resolved `{success:false}` from the server is a real
 * answer and is returned as-is on the first try, not retried. Generic over any register/waitlist
 * action call, so any call site invoking one of these actions can share it.
 *
 * @param action - Thunk invoking a register/waitlist action call for one slot.
 * @returns The action's resolved value, retried once on rejection.
 * @throws The retry's rejection reason if it also fails.
 */
export async function withOneRetryOnRejection<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch {
    await new Promise((resolve) => setTimeout(resolve, REGISTER_NETWORK_RETRY_DELAY_MS));
    return action();
  }
}

/**
 * Send one consolidated confirmation email per outcome (registered, waitlisted)
 * after a multi-slot signup batch. Single-slot batches are skipped so the
 * per-action send fires as usual.
 *
 * @param rs - Settled per-slot results.
 * @param isMultiSlot - Whether the batch had more than one slot (skips otherwise).
 * @param activityId - Activity the registrations belong to.
 * @returns Resolves once the consolidated sends settle (errors are logged, not thrown).
 */
export async function sendConsolidatedEmailsForBatch(
  rs: RegisterPromiseResult[],
  isMultiSlot: boolean,
  activityId: number
): Promise<void> {
  if (!isMultiSlot) return;
  const registeredIds: number[] = [];
  const waitlistedIds: number[] = [];
  for (const r of rs) {
    if (r.status !== "fulfilled" || !r.value.success || !r.value.registrationId) continue;
    if (r.value.waitlisted) waitlistedIds.push(r.value.registrationId);
    else registeredIds.push(r.value.registrationId);
  }
  // Fire each kind separately so a mixed batch still gets two emails (one per
  // outcome), but each email lists every slot of that outcome.
  const sends: Promise<unknown>[] = [];
  if (registeredIds.length > 0) {
    sends.push(sendMultiSlotConfirmationEmail({ activityId, kind: "confirmation", registrationIds: registeredIds }));
  }
  if (waitlistedIds.length > 0) {
    sends.push(sendMultiSlotConfirmationEmail({ activityId, kind: "waitlist_joined", registrationIds: waitlistedIds }));
  }
  try {
    await Promise.allSettled(sends);
  } catch (err) {
    console.error("sendConsolidatedEmailsForBatch: multi-slot email send failed", err);
  }
}
